/** @jsxImportSource ./simboluri-jsx */
// VEDEREA 3D A OCHIULUI DE PLACĂ.
//
// Până azi, pagina unui ochi arăta în dreapta sus… clădirea întreagă, cu
// ochiul aprins pe ea — adică EXACT cardul „Contextul în clădire" de dedesubt:
// aceeași clădire, de două ori, și nicăieri elementul despre care e pagina.
// Aici se desenează PIESA: felia de placă cu conturul ei real (cu goluri și
// inele), plasa de jos bară cu bară la pasul dimensionat, călăreții de sus pe
// reazeme și reazemele fizice de sub laturi (grinda sau peretele).
//
// Ce se desenează e numai ce EXISTĂ în rezultate (v. memoria „în 3D se
// desenează doar ce e fizic"):
//   * conturul, golurile și laturile clasificate vin din Pasul 7 (`FemPanou`);
//   * grosimea, acoperirea, diametrele și pașii vin din Pasul 16 (`Ochi`);
//   * lungimea călăreților e 0,25·l — chiar fracțiunea cu care extrasul de
//     armătură îi numără (`extras_armare.FRACT_CALARET`), nu o alegere de
//     desen.
//
// Desenul stă în axele GRILEI (X/Y ale clădirii, cele pe care s-au măsurat
// deschiderile și s-au dimensionat plasele): conturul se rotește cu −θ, altfel
// barele „pe direcția X" ar apărea strâmbe pe o clădire rotită.

import { useEffect, useRef, useState } from "react";
import { useT } from "../i18n";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { incadreaza } from "./incadrare3d";
import { FemPanou } from "./tipuri";
import { CULOARE_REAZEM, Ochi } from "./StepArmarePlaca";

// culorile reazemelor sunt ALE PLANULUI (`CULOARE_REAZEM`): planul și 3D-ul
// ochiului stau unul lângă altul, deci nu pot avea două limbaje de culoare
const hex = (css: string) => parseInt(css.replace("#", ""), 16);

const CULOARE = {
  beton: 0x9aa0a8,
  muchie: 0x777777,
  // aceleași ca barele din planul ochiului: roșu = X, verde = Y
  jos_x: 0xb5323d,
  jos_y: 0x2e7d72,
  // călăreții — violetul plasei de sus din secțiunea prin grosime
  sus: 0x6b4fa1,
  grinda: hex(CULOARE_REAZEM.grinda),
  perete: hex(CULOARE_REAZEM.perete),
  liber: hex(CULOARE_REAZEM.contur_fara_reazem),
  gol: 0xa97614,
  zid: 0x76858a,
};

// cât de dese pot fi barele DESENATE: peste atât, desenul arată una din k
// (armătura rămâne cea din text — aceeași regulă ca în planul ochiului)
const MAX_BARE = 44;

type Pct = [number, number];

// Intervalele în care o dreaptă axată taie ochiul (conturul MINUS goluri):
// regula par–impar peste intersecțiile cu toate inelele. Cu ea, plasa se
// oprește singură la marginea unui ochi în L și ocolește golul de scară.
function intervale(inele: Pct[][], t: number, deLungulX: boolean): [number, number][] {
  const taieturi: number[] = [];
  for (const inel of inele) {
    for (let i = 0; i < inel.length; i++) {
      const a = inel[i];
      const b = inel[(i + 1) % inel.length];
      // «0» = coordonata pe care o fixează dreapta, «1» = cea de-a lungul ei
      const a0 = deLungulX ? a[1] : a[0];
      const a1 = deLungulX ? a[0] : a[1];
      const b0 = deLungulX ? b[1] : b[0];
      const b1 = deLungulX ? b[0] : b[1];
      // comparație semi-deschisă: un vârf care cade exact pe dreaptă se
      // numără o singură dată, altfel par–impar-ul se încurcă la colțuri
      if ((a0 <= t && b0 > t) || (b0 <= t && a0 > t))
        taieturi.push(a1 + ((t - a0) / (b0 - a0)) * (b1 - a1));
    }
  }
  taieturi.sort((p, q) => p - q);
  const out: [number, number][] = [];
  for (let i = 0; i + 1 < taieturi.length; i += 2)
    if (taieturi[i + 1] - taieturi[i] > 0.06)
      out.push([taieturi[i], taieturi[i + 1]]);
  return out;
}

function nr(v: number | undefined | null, zecimale = 1): string {
  if (v === undefined || v === null || !isFinite(v)) return "—";
  return v.toFixed(zecimale).replace(".", ",");
}

export default function Ochi3D({
  ochi,
  panou,
  theta = 0,
  acoperire_mm = 20,
  height = 320,
}: {
  ochi: Ochi;
  panou: FemPanou;
  // unghiul grilei (model.theta_rad): conturul e în axele globale, plasele în
  // ale grilei
  theta?: number;
  acoperire_mm?: number;
  height?: number;
}) {
  const mount = useRef<HTMLDivElement>(null);
  const [rarit, setRarit] = useState(1);

  useEffect(() => {
    const gazda = mount.current;
    if (!gazda) return;
    const contur0 = (panou.contur ?? []) as Pct[];
    if (contur0.length < 3) return;

    const W = gazda.clientWidth || 360;
    const H = height;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf3f6f9);
    const camera = new THREE.PerspectiveCamera(38, W / H, 0.01, 400);
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      preserveDrawingBuffer: true,
    });
    renderer.setSize(W, H);
    renderer.setPixelRatio(window.devicePixelRatio || 1);
    gazda.appendChild(renderer.domElement);
    const gunoi: { dispose(): void }[] = [];

    // ---- geometria plană, în axele grilei și centrată în origine ----------
    const cth = Math.cos(theta);
    const sth = Math.sin(theta);
    const laGrila = (p: Pct): Pct => [
      cth * p[0] + sth * p[1],
      -sth * p[0] + cth * p[1],
    ];
    const brut = contur0.map(laGrila);
    const cx = (Math.min(...brut.map((p) => p[0])) + Math.max(...brut.map((p) => p[0]))) / 2;
    const cy = (Math.min(...brut.map((p) => p[1])) + Math.max(...brut.map((p) => p[1]))) / 2;
    const mut = (p: Pct): Pct => [p[0] - cx, p[1] - cy];
    const contur = brut.map(mut);
    const gauri: Pct[][] = [
      ...(panou.inele ?? []).map((i) => i.contur as Pct[]),
      ...(panou.zone_gol ?? []).map((z) => (z.contur ?? []) as Pct[]),
    ]
      .filter((g) => (g?.length ?? 0) >= 3)
      .map((g) => g.map((p) => mut(laGrila(p))));
    const inele = [contur, ...gauri];
    const xs = contur.map((p) => p[0]);
    const ys = contur.map((p) => p[1]);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);

    const h = Math.max(ochi.h_cm, 5) / 100;
    const ac = acoperire_mm / 1000;

    // ---- betonul: conturul extrudat pe grosime, cu golurile decupate ------
    const forma = new THREE.Shape(contur.map((p) => new THREE.Vector2(p[0], p[1])));
    for (const g of gauri)
      forma.holes.push(new THREE.Path(g.map((p) => new THREE.Vector2(p[0], p[1]))));
    const geoB = new THREE.ExtrudeGeometry(forma, { depth: h, bevelEnabled: false });
    gunoi.push(geoB);
    const matB = new THREE.MeshStandardMaterial({
      color: CULOARE.beton,
      transparent: true,
      opacity: 0.22,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    gunoi.push(matB);
    const beton = new THREE.Mesh(geoB, matB);
    beton.position.z = -h;      // fața de SUS a plăcii la z = 0
    scene.add(beton);
    const geoM = new THREE.EdgesGeometry(geoB);
    gunoi.push(geoM);
    const matM = new THREE.LineBasicMaterial({ color: CULOARE.muchie });
    gunoi.push(matM);
    const muchii = new THREE.LineSegments(geoM, matM);
    muchii.position.z = -h;
    scene.add(muchii);

    // marginea golurilor, îngroșată (chihlimbar, ca hașura din plan)
    for (const g of gauri) {
      const pts = g.map((p) => new THREE.Vector3(p[0], p[1], -h / 2));
      pts.push(pts[0].clone());
      const geo = new THREE.BufferGeometry().setFromPoints(pts);
      gunoi.push(geo);
      const mat = new THREE.LineBasicMaterial({ color: CULOARE.gol });
      gunoi.push(mat);
      scene.add(new THREE.Line(geo, mat));
    }

    // ---- REAZEMELE: ce ține fiecare latură, sub placă ---------------------
    for (const lat of panou.laturi ?? []) {
      const a = mut(laGrila(lat.a as Pct));
      const b = mut(laGrila(lat.b as Pct));
      const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (L < 0.05) continue;
      const unghi = Math.atan2(b[1] - a[1], b[0] - a[0]);
      const mx = (a[0] + b[0]) / 2;
      const my = (a[1] + b[1]) / 2;
      if (lat.reazem === "contur_fara_reazem") {
        // nu se desenează nimic dedesubt — nu există nimic: doar muchia,
        // marcată cu linie întreruptă roșie
        const geo = new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(a[0], a[1], -h),
          new THREE.Vector3(b[0], b[1], -h),
        ]);
        gunoi.push(geo);
        const mat = new THREE.LineDashedMaterial({
          color: CULOARE.liber, dashSize: 0.25, gapSize: 0.18,
        });
        gunoi.push(mat);
        const l = new THREE.Line(geo, mat);
        l.computeLineDistances();
        scene.add(l);
        continue;
      }
      const grinda = lat.reazem === "grinda";
      const t = Math.max(Number(lat.t_reazem_m ?? 0) || (grinda ? 0.25 : 0.2), 0.12);
      const adanc = grinda ? 0.4 : 0.55;
      const geo = new THREE.BoxGeometry(L, t, adanc);
      gunoi.push(geo);
      const mat = new THREE.MeshStandardMaterial({
        color: grinda ? CULOARE.grinda : CULOARE.perete,
        transparent: true,
        opacity: 0.5,
      });
      gunoi.push(mat);
      const m = new THREE.Mesh(geo, mat);
      m.position.set(mx, my, -h - adanc / 2);
      m.rotation.z = unghi;
      scene.add(m);
    }

    // pereții care stau PE placă (fără grindă dedesubt) — încarcă ochiul,
    // deci fac parte din piesa asta
    for (const s of panou.pereti_pe_placa ?? []) {
      const a = mut(laGrila(s.a as Pct));
      const b = mut(laGrila(s.b as Pct));
      const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (L < 0.2) continue;
      const geo = new THREE.BoxGeometry(L, 0.15, 0.7);
      gunoi.push(geo);
      const mat = new THREE.MeshStandardMaterial({
        color: CULOARE.zid, transparent: true, opacity: 0.28, depthWrite: false,
      });
      gunoi.push(mat);
      const m = new THREE.Mesh(geo, mat);
      m.position.set((a[0] + b[0]) / 2, (a[1] + b[1]) / 2, 0.35);
      m.rotation.z = Math.atan2(b[1] - a[1], b[0] - a[0]);
      scene.add(m);
    }

    // ---- PLASELE ---------------------------------------------------------
    let raritMax = 1;
    // un set de bare paralele cu direcția `d`, înșirate la pasul real pe
    // direcția perpendiculară, tăiate pe conturul ochiului
    const set = (
      d: "x" | "y",
      pas_cm: number,
      phi_mm: number,
      z: number,
      culoare: number,
      limite?: [number, number][]
    ) => {
      const pas = pas_cm / 100;
      const phi = Math.max(phi_mm, 4) / 1000;
      if (!(pas > 0.02)) return;
      const deLungulX = d === "x";
      const t0 = deLungulX ? minY : minX;
      const t1 = deLungulX ? maxY : maxX;
      let k = 1;
      while ((t1 - t0) / (pas * k) > MAX_BARE) k += 1;
      raritMax = Math.max(raritMax, k);
      const geo = new THREE.CylinderGeometry(phi / 2, phi / 2, 1, 6);
      gunoi.push(geo);
      const mat = new THREE.MeshStandardMaterial({ color: culoare });
      gunoi.push(mat);
      for (let t = t0 + (pas * k) / 2; t < t1; t += pas * k) {
        for (const [u0, u1] of intervale(inele, t, deLungulX)) {
          for (const [v0, v1] of limite ?? [[u0, u1]]) {
            const a = Math.max(u0, v0);
            const b = Math.min(u1, v1);
            if (b - a < 0.1) continue;
            const m = new THREE.Mesh(geo, mat);
            m.scale.y = b - a;
            // axa cilindrului e Y: pentru o bară pe X se culcă cu 90°
            if (deLungulX) m.rotation.z = Math.PI / 2;
            m.position.set(
              deLungulX ? (a + b) / 2 : t,
              deLungulX ? t : (a + b) / 2,
              z
            );
            scene.add(m);
          }
        }
      }
    };

    const ds = ochi.schema.dir_scurt === "y" ? "y" : "x";
    const dl = ds === "x" ? "y" : "x";
    const phiJos = (d: string) => (ochi.armare[`jos_${d}`]?.phi_mm ?? 8) / 1000;
    // plasa de JOS: direcția scurtă stă dedesubt (brațul de pârghie mai mare),
    // cea lungă imediat peste ea — exact ipoteza cu care s-a calculat d
    for (const [d, rang] of [[ds, 0], [dl, 1]] as [("x" | "y"), number][]) {
      const p = ochi.armare[`jos_${d}`];
      if (!p) continue;
      const z = -h + ac + (rang === 0 ? phiJos(ds) / 2 : phiJos(ds) + phiJos(d) / 2);
      set(d, p.pas_cm, p.phi_mm, z, d === "x" ? CULOARE.jos_x : CULOARE.jos_y);
    }
    // CĂLĂREȚII de sus: numai pe capetele REZEMATE, pe 0,25·l de la reazem.
    // Banda se măsoară de la marginea GABARITULUI pe direcția aia — aceeași
    // simplificare cu care îi numără extrasul de armătură; la un ochi în L
    // marginea gabaritului nu e peste tot reazemul real.
    for (const [d, rang] of [[ds, 0], [dl, 1]] as [("x" | "y"), number][]) {
      const p = ochi.armare[`sus_${d}`];
      if (!p) continue;
      const dir = d === "x" ? ochi.schema.x : ochi.schema.y;
      const banda = 0.25 * (dir.l_m || (d === "x" ? maxX - minX : maxY - minY));
      const lo = d === "x" ? minX : minY;
      const hi = d === "x" ? maxX : maxY;
      const limite: [number, number][] = [];
      if (dir.i?.rezemat || dir.i?.continuu) limite.push([lo, lo + banda]);
      if (dir.j?.rezemat || dir.j?.continuu) limite.push([hi - banda, hi]);
      if (!limite.length) continue;
      const phiS = (ochi.armare[`sus_${ds}`]?.phi_mm ?? 8) / 1000;
      const z = -ac - (rang === 0 ? phiS / 2 : phiS + p.phi_mm / 2000);
      set(d, p.pas_cm, p.phi_mm, z, CULOARE.sus, limite);
    }
    setRarit(raritMax);

    scene.add(new THREE.AmbientLight(0xffffff, 0.95));
    const lum = new THREE.DirectionalLight(0xffffff, 0.6);
    lum.position.set(2, -3, 5);
    scene.add(lum);

    // `camera.up` ÎNAINTE de OrbitControls (constructorul îngheață axa de
    // orbitare), apoi încadrarea comună
    camera.up.set(0, 0, 1);
    const gab = Math.max(maxX - minX, maxY - minY, 1);
    camera.position.set(-gab * 0.55, -gab * 0.95, gab * 0.75);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, 0, -h / 2);
    incadreaza(camera, controls, scene);

    const render = () => renderer.render(scene, camera);
    controls.addEventListener("change", render);
    render();
    return () => {
      controls.removeEventListener("change", render);
      controls.dispose();
      gunoi.forEach((x) => x.dispose());
      renderer.dispose();
      gazda.removeChild(renderer.domElement);
    };
  }, [ochi, panou, theta, acoperire_mm, height]);

  const t = useT();

  if ((panou.contur ?? []).length < 3) {
    return (
      <div className="sg-muted" style={{ fontSize: 12 }}>
        {t("struct.ochi.fara_contur")}
      </div>
    );
  }
  const ds = ochi.schema.dir_scurt;
  return (
    <div>
      <div ref={mount} style={{ width: "100%", height }} />
      <div className="sg-muted" style={{ fontSize: 11, lineHeight: 1.5, paddingTop: 6 }}>
        {t("struct.ochi.legenda", {
          h: nr(ochi.h_cm, 0), acoperire: nr(acoperire_mm / 10, 1),
        })}
        <span style={{ color: "#b5323d" }}>■ X</span>{" "}
        {ochi.armare["jos_x"]?.text ?? "—"} ·{" "}
        <span style={{ color: "#2e7d72" }}>■ Y</span>{" "}
        {ochi.armare["jos_y"]?.text ?? "—"}
        {t("struct.ochi.dir_scurta", { dir: ds.toUpperCase() })}
        <span style={{ color: "#6b4fa1" }}>{t("struct.ochi.calareti")}</span>
        {t("struct.ochi.calareti_text")}
        <span style={{ color: CULOARE_REAZEM.grinda }}>{t("struct.ochi.grinda")}</span>,{" "}
        <span style={{ color: CULOARE_REAZEM.perete }}>{t("struct.ochi.perete")}</span>
        {t("struct.ochi.fara_reazem")}
        {rarit > 1 ? t("struct.ochi.rarit", { n: rarit }) : ""}
      </div>
    </div>
  );
}
